// =============================================================================
// ui/tabs/SubscriptionCard.tsx — the plan card on My Account. #65.
// -----------------------------------------------------------------------------
// Relocated from the old client's `wireSubscriptionCard` (app/index.html:49212),
// which is the one place in the app that asks for money.
//
// ── 🚨 THREE STATES, NOT TWO, AND THE THIRD IS THE EXPENSIVE ONE ──
//
// "We could not read your subscription" is not "you do not have one". Showing
// the Free upsell — complete with a live buy button — to somebody who may
// already be paying is the single most expensive thing this card can do: they
// buy again and end up with two live subscriptions. The old client's free-tier
// suite spends assertions on exactly this, and `STRIPE-BILLING-PLAN.md` §5
// hazard 4 is the same failure reached from the server side.
//
// So the unknown branch carries NO buy button and makes NO "Free tier" claim.
// It offers a retry, because nothing else re-reads entitlement after boot.
//
// ── ⚠ HIDDEN DURING BETA, THE OLD CLIENT'S RULE, VERBATIM ──
//
//   const visible = PAID_LAUNCH || _authEmail === 'joshtradesfutures@gmail.com';
//
// Offering to take money for a product that has not launched is worse than
// showing nothing. Flipping `PAID_LAUNCH` reveals it to everyone, so there is
// nothing to remove later.
//
// ── ⚠ WHAT IS DELIBERATELY NOT PORTED, AND WHY ──
//
// The old client's free panel opens with the reader's OWN #1 ranked leak, in
// dollars. Producing it needs `rankLeaks` over the whole book — the widest read
// in this client per row — and the old client gets it free only because it holds
// every trade in memory already. Adding that read to My Account for one sentence
// would make the slowest read in the app a cost of opening the account screen.
//
// What renders instead is the old client's OWN copy for the same card when it
// cannot rank a leak yet. It is its wording, not an invention. Tracked in #65.
//
// 🔴 NO "LIFETIME PRICE" CLAIM. Deliberately removed from the old app and kept
// out by a live assertion there. See domain/billing.ts.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { openBillingPortal, startCheckout, type BillingInterval } from '../../io/billing/checkout';
import { ComparePlans } from './ComparePlans';
import {
  BILLING_PLANS, founderAnnualSaving, planPriceOnly,
} from '../../selectors/billing';
import { FREE_ACCOUNT_LIMIT } from '../../selectors/freeTier';
import { PAID_LAUNCH, entitlementIsLive } from '../../selectors/entitlement';
import type { EntitlementTier } from '../../selectors/entitlement';
import { useAccountCap } from '../state/useFreeTierCap';
import { useAuthUser } from '../state/useAuthUser';
import { useEntitlement } from '../state/useEntitlement';
import { useSupabase } from '../state/supabase';
// ⚠ `moneyPlain`, NOT `money`. The only figure this card formats is a SAVING,
// and `money` prefixes a `+` to anything positive — which turns "$110 you do not
// spend" into "$110 you gain". `money` is not imported here at all, so the next
// edit cannot reach for it by accident.
import { moneyPlain } from '../format';

/**
 * ⚠ The beta escape hatch, and the only hardcoded address in this client. It is
 * the old client's, unchanged, so the two agree about who can see the card
 * before launch.
 */
const BETA_VIEWER = 'joshtradesfutures@gmail.com';

/**
 * ── 🔴 THIS WATCHED FOR A PARAMETER THAT NEVER ARRIVES ──
 *
 * The checkout function builds the return address itself and it is NOT
 * overridable by the browser — deliberately, because a caller-supplied success
 * URL is an open redirect and "they reached /success so they must have paid" is
 * the classic place people wrongly provision access. It sends:
 *
 *     /?billing=success&session={CHECKOUT_SESSION_ID}
 *         supabase/functions/stripe-checkout/logic.ts:200
 *
 * Both clients watched for `?checkout=` instead. The old client even passes a
 * `successUrl` of its own, which the server ignores. So the "Payment received —
 * finishing your upgrade" screen below could never appear after a real purchase,
 * and what a customer saw in the seconds after their card cleared was the free
 * tier pitch with a live buy button.
 *
 * ⚠ That window is not always seconds. When a price id is misconfigured the
 * webhook deliberately throws so Stripe retries — for up to three days — and
 * the buy button is live for all of it. The server does refuse a SECOND purchase
 * once a row exists, but in this gap there is no row yet.
 *
 * ⚠ THE MARKER STILL PROVES NOTHING. It only starts the wait. Access comes from
 * the webhook and nowhere else, which is why the branch below says "finishing"
 * rather than "you're Pro".
 */
const RETURN_PARAM = 'billing';
const RETURN_SUCCESS = 'success';

function hasCheckoutReturn(): boolean {
  try {
    return new URLSearchParams(window.location.search).get(RETURN_PARAM) === RETURN_SUCCESS;
  } catch { return false; }
}

/**
 * ⚠ Take the parameter out of the URL, so a refresh does not restart the wait —
 * and so the address bar stops advertising a completed purchase.
 */
function stripCheckoutParam(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(RETURN_PARAM)) return;
    params.delete(RETURN_PARAM);
    // ⚠ The session id rides along on the same redirect. It grants nothing, but
    // leaving it in the address bar advertises a completed purchase in every
    // screenshot and every shared link.
    params.delete('session');
    const q = params.toString();
    window.history.replaceState(
      {}, '', window.location.pathname + (q ? `?${q}` : '') + window.location.hash,
    );
  } catch { /* a nicety; never worth failing the card over */ }
}

/**
 * What to call the plan on screen.
 *
 * ⚠ NULL IS "Pro", NOT A BLANK. See the note at the call site: the column is
 * free text with no constraint, and the "every live row has a plan" guarantee
 * holds only for rows the purchase webhook wrote.
 */
function planLabel(tier: EntitlementTier | null): string {
  return tier === 'founder' ? 'Founder' : 'Pro';
}

/**
 * 🚨 THE LINE THAT WAS A REFUND CONVERSATION.
 *
 * One date, two opposite meanings. If they have cancelled, `currentPeriodEnd` is
 * the day their access ENDS; otherwise it is the day they are charged again.
 * The card printed "Renews" over both.
 *
 * ⚠ A trial is called a trial. "Renews" on a trial hides the fact that the first
 * charge has not happened yet, which is the one thing a trialist wants to know.
 */
function renewalLine(
  row: { cancelAtPeriodEnd: boolean; status: string | null }, when: string | null,
): string {
  if (row.cancelAtPeriodEnd) {
    return when ? `Cancelled — access until ${when}` : 'Cancelled — ends at the end of this period';
  }
  // 🚨 A RENEWAL THAT IS ALREADY FAILING IS NOT A RENEWAL. The date on a
  // past-due row is the end of the period they last paid for, which by
  // definition has arrived — so "Renews 3 March" is a promise about a date in
  // the past, sitting directly above a warning that the payment bounced. The
  // issue names this exactly: "a payment that is currently failing gets the
  // same Renews line with a date already in the past".
  if (isPaymentProblem(row.status)) return 'Payment overdue — retrying';
  if (row.status === 'trialing') return when ? `Trial — first charge ${when}` : 'Trial';
  return when ? `Renews ${when}` : 'Active';
}

/**
 * A payment the provider is retrying.
 *
 * 🔴 NEVER PRINT THE RAW WORD. A customer should not read "past_due", and the
 * provider's vocabulary is not this app's to expose — a REFUNDED subscription
 * can still carry the word "active", which is why `revokedAt` and not this
 * decides who gets in.
 */
function isPaymentProblem(status: string | null): boolean {
  return status === 'past_due' || status === 'unpaid';
}

/** One rung of the Spot-it / Fix-it / Prove-it ladder. */
function Step({ on, title, sub }: { on: boolean; title: string; sub: string }) {
  return (
    <div className="sub-step">
      <span className={`sub-step-dot${on ? ' on' : ''}`} aria-hidden="true">{on ? '✓' : '🔒'}</span>
      <div>
        <div className={`sub-step-title${on ? '' : ' off'}`}>{title}</div>
        <div className="sub-step-sub">{sub}</div>
      </div>
    </div>
  );
}

export function SubscriptionCard() {
  const { sb } = useSupabase();
  const { read, settled, reload } = useEntitlement();
  const { user } = useAuthUser();
  const { count: accountCount } = useAccountCap();
  const [busy, setBusy] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activating, setActivating] = useState(() => hasCheckoutReturn());
  // ⚠ Once only. Without the latch the effect re-arms on every re-read it
  // itself causes, and the card waits for ever.
  const polled = useRef(false);

  /**
   * Poll for the webhook, then give up gracefully.
   *
   * ⚠ FIVE TRIES, 1.5s apart — the old client's numbers. The URL parameter is
   * stripped first so a refresh does not restart the wait.
   */
  useEffect(() => {
    if (!activating || polled.current) return;
    polled.current = true;
    stripCheckoutParam();
    let cancelled = false;
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      if (++tries > 5) { setActivating(false); return; }
      reload();
      window.setTimeout(tick, 1500);
    };
    window.setTimeout(tick, 1500);
    return () => { cancelled = true; };
  }, [activating, reload]);

  // The row arrived — stop waiting and let the active branch render.
  useEffect(() => {
    if (activating && !read.unknown && read.row?.isActive) setActivating(false);
  }, [activating, read]);

  if (!PAID_LAUNCH && user?.email !== BETA_VIEWER) return null;

  // ⚠ Nothing at all until the first read settles. A card that says "Free tier"
  // for a moment and then corrects itself has already offered to charge a
  // paying customer.
  if (!settled) return null;

  // 🚨 THEY HAVE JUST PAID AND THE WEBHOOK MAY STILL BE IN FLIGHT.
  //
  // Stripe redirects back here the moment the card clears, but entitlement is
  // written by the WEBHOOK — a different request, arriving whenever it arrives.
  // Rendering the free branch in that gap shows a buy button to somebody who
  // paid thirty seconds ago, which is the most alarming thing this screen could
  // possibly do.
  //
  // ⚠ It never claims success on the redirect alone. Either the row turns up and
  // the active branch renders itself, or the wait times out and the card says
  // "activating shortly" — which is true, where "You're Pro" would be a guess.
  if (activating) {
    return (
      <div className="card sub-card" data-testid="subscription-card" data-state="activating">
        <div className="sub-head"><span className="sub-tier-pill">Subscription</span></div>
        <div className="sub-line">Payment received — finishing your upgrade…</div>
        <p className="muted sub-note">
          This usually takes a few seconds. You don’t need to pay again, and
          nothing is lost if you navigate away.
        </p>
      </div>
    );
  }

  const go = async (fn: () => ReturnType<typeof startCheckout>) => {
    setBusy(true);
    setError(null);
    const r = await fn();
    setBusy(false);
    if (r.ok) { window.location.href = r.url; return; }
    // ⚠ Sold-out is not an error the user caused, and the wording says so —
    // "you have NOT been charged" is the part that matters to them.
    setError(r.error);
  };

  const row = read.unknown ? null : read.row;
  const live = entitlementIsLive(row, Date.now());

  // ── 1. we could not tell ───────────────────────────────────────────────────
  if (read.unknown) {
    return (
      <div className="card sub-card" data-testid="subscription-card" data-state="unknown">
        <div className="sub-head"><span className="sub-tier-pill">Subscription</span></div>
        <div className="sub-line">We couldn’t load your subscription just now.</div>
        <p className="muted sub-note">
          Your access is unaffected — nothing has been locked. This is a connection
          problem on our side, not a change to your plan.
        </p>
        <div className="sub-actions">
          <button type="button" className="btn" onClick={reload}>Retry</button>
        </div>
      </div>
    );
  }

  // ── 2. they are paying ─────────────────────────────────────────────────────
  if (row?.isActive) {
    const when = row.currentPeriodEnd
      ? new Date(row.currentPeriodEnd).toLocaleDateString() : null;
    // 🔴 THE WORD "Pro" WAS TYPED INTO THE MARKUP TWICE, so a Founder — who is
    // paying a rate held for as long as they stay subscribed — was told they
    // were on the standard plan. The plan word has been on every row all along;
    // the card simply never asked for it.
    //
    // ⚠ NULL READS AS "Pro", which is the old client's fallback and the right
    // one: the column has no constraint, so a seat granted by hand or a support
    // correction can carry no plan at all, and a blank badge would be worse than
    // the generic paid name.
    const plan = planLabel(row.tier);
    return (
      <div className="card sub-card" data-testid="subscription-card" data-state="active">
        <div className="sub-head"><span className="sub-tier-pill">{plan}</span></div>
        <div className="sub-line">You’re on <b className="sub-brand">{plan}</b>.</div>
        {/* 🚨 "RENEWS" AND "ENDS" ARE THE SAME DATE AND OPPOSITE FACTS. Somebody
            who has already cancelled was being told their subscription renews on
            the day it dies — the one sentence on this screen that turns into a
            refund conversation. ⚠ The old client prints "renews … · cancels at
            period end" in one line, contradicting itself; this picks one. */}
        <p className="muted sub-note">{renewalLine(row, when)}</p>
        {/* ⚠ A FAILING CARD IS NOT A CANCELLATION and must not read like one.
            Access deliberately continues while the provider retries — cutting
            it mid-retry churns customers who would have recovered — so this
            says what is wrong and what to do, and nothing is locked. */}
        {isPaymentProblem(row.status) ? (
          <p className="muted sub-warn">
            ⚠ Your last payment didn’t go through. Your access continues while
            it retries — open the portal to update your card.
          </p>
        ) : null}
        {/* ⚠ An active row that the RULE says is stale. Saying nothing here
            leaves them looking at "You're on Pro" while Pro tabs are locked. */}
        {!live ? (
          <p className="muted sub-warn">
            ⚠ We can’t confirm this is current, so some features may be locked.
            Open the portal to check your billing.
          </p>
        ) : null}
        <div className="sub-actions">
          <button
            type="button" className="btn" disabled={busy}
            onClick={() => void go(() => openBillingPortal(sb))}
          >
            Manage subscription
          </button>
        </div>
        {error ? <p className="sub-error" role="alert">{error}</p> : null}
      </div>
    );
  }

  // ── 3. a real free user ────────────────────────────────────────────────────
  const founder = BILLING_PLANS.founder_annual;
  return (
    <div className="card sub-card" data-testid="subscription-card" data-state="free">
      <div className="sub-head"><span className="sub-tier-pill">Free tier</span></div>
      <div className="sub-pair">
        <div>
          <div className="sub-line">You’re on the <b className="sub-brand">Free Tier</b></div>
          <p className="muted sub-note">
            Log or import a few trades and the first step starts working straight
            away — no setup.
          </p>
          {/* 🚨 NULL IS NOT ZERO, AND HERE IT FINALLY SHOWS. `useFreeTierCap`
              reports null when the accounts read has not landed or has failed;
              printing "0 of 1 accounts used" then is a confident wrong number on
              the one screen where the user is deciding whether to pay. */}
          {accountCount === null ? null : (
            <div className="muted sub-usage">
              Free plan · {accountCount} of {FREE_ACCOUNT_LIMIT}
              {FREE_ACCOUNT_LIMIT === 1 ? ' account' : ' accounts'} used
            </div>
          )}
          <Step on title="Spot it" sub="Your #1 performance leak, in dollars — included on Free" />
          <Step on={false} title="Fix it" sub="Write one conditional rule that targets that leak" />
          <Step on={false} title="Prove it" sub="Adherence tracking that shows whether the rule moved your numbers" />
        </div>
        <aside className="sub-offer">
          <div className="so-tag">Founder rate · first 100</div>
          <div className="so-price">{planPriceOnly(founder)}<small> /yr</small></div>
          {/* ⚠ ARITHMETIC over the real prices, never a typed "$110". */}
          <div className="so-delta">{moneyPlain(founderAnnualSaving(), 0)} less than Pro, every year</div>
          <p className="so-note">
            Your rate never rises while you stay subscribed. After the first 100,
            Founder closes for good.
          </p>
          <button
            type="button" className="btn primary so-buy" disabled={busy}
            onClick={() => void go(() => startCheckout(sb, { interval: 'annual', founder: true }))}
          >
            Unlock Fix &amp; Prove — {founder.price}
          </button>
          {/* ⚠ Two actions, as in the old client: a ready buyer goes straight to
              checkout, an undecided one compares first. */}
          <button type="button" className="so-cmp" onClick={() => setComparing(true)}>
            Compare plans →
          </button>
        </aside>
      </div>
      {error ? <p className="sub-error" role="alert">{error}</p> : null}
      <p className="muted sub-guarantee">7-day money-back guarantee · cancel anytime</p>
      {/* ⚠ The comparison is a MODAL over this card, not a second copy of the
          offer on it. The old client learned that a differently-worded second
          copy is exactly how the two drift apart. */}
      {comparing ? (
        <ComparePlans
          busy={busy}
          onClose={() => setComparing(false)}
          onBuy={(args: { interval: BillingInterval; founder: boolean }) => {
            setComparing(false);
            void go(() => startCheckout(sb, args));
          }}
        />
      ) : null}
    </div>
  );
}
